import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from "@angular/common/http";
import {ExportPdfService} from "../../shared/service/files/export-pdf.service";
import {GridGetService, GridServiceParams, GridServiceResponse} from "../../shared/model/page-grid";
import {TramsTripResponse} from "../model/trams-trip";
import {catchError, map, Observable} from "rxjs";
import {removeEmptyParams} from "../../../infrastructure/helper/remove-empty-params";
import moment from 'moment';

@Injectable({
  providedIn: 'root'
})
export class TramsTripGridService implements GridGetService<TramsTripResponse> {
  private readonly baseUrl = 'dbov2/fms-trams-trips';
  private readonly http = inject(HttpClient);
  private readonly pdfService = inject(ExportPdfService);
  private readonly DATE_FORMAT = 'YYYY-MM-DD';
  private getAutocompleteValue(value: any): string {
    if (value && typeof value === 'object' && 'value' in value) {
      return value.value;
    }
    return value || '';
  }
  getItems({filters, sort, page}: GridServiceParams): Observable<GridServiceResponse<TramsTripResponse>> {
    const sortParam = sort ? { ...sort } : {};
    const params: Record<string, string | number | boolean> = {
      PageSize: page?.pageSize || 25,
      PageNumber: (page?.pageIndex || 0) + 1,
      PropertyName: sortParam?.active || 'tripId',
      OrderKey: sortParam?.direction?.toUpperCase() || 'DESC',

      AdxTripReference: this.getAutocompleteValue(filters?.['adxTripReference']),
      TramsRecordLocator: this.getAutocompleteValue(filters?.['tramsRecordLocator']),
      FmsDataSourceId: filters?.['fmsDataSourceId'] ?? '',
      FmsTripStartDateFrom: filters?.['fmsTripStartDate'] ? moment(filters?.['fmsTripStartDate']).format(this.DATE_FORMAT) : '',
      FmsTripStartDateTo: filters?.['fmsTripEndDate'] ? moment(filters?.['fmsTripEndDate']).format(this.DATE_FORMAT) : '',
    };

    removeEmptyParams(params);

    return this.http.get<GridServiceResponse<TramsTripResponse>>(`${this.baseUrl}`, {params: {...params}})
      .pipe(
        map(response => {
          return response;
        }),
        catchError((error: HttpErrorResponse) => {
          throw new Error(error?.message || 'Server error');
        }));
  }
  getPdf({filters, page}: GridServiceParams): Observable<void> {
    const params: Record<string, string | number | boolean> = {
      exportToPdf: true,
      format: 'pdf',
      formatCurrency: 'true',
      hideCurrencySymbol: 'true',
      currencyCode: 'USD',
      currencySymbol: '$',
      currencyFormat: '0.00',
      PropertyName: 'tripId',
      OrderKey: 'desc',
      PageSize: page?.totalItemCount || 100,

      AdxTripReference: this.getAutocompleteValue(filters?.['adxTripReference']),
      TramsRecordLocator: this.getAutocompleteValue(filters?.['tramsRecordLocator']),
      FmsDataSourceId: filters?.['fmsDataSourceId'] ?? '',
      FmsTripStartDateFrom: filters?.['fmsTripStartDate'] ? moment(filters?.['fmsTripStartDate']).format(this.DATE_FORMAT) : '',
      FmsTripStartDateTo: filters?.['fmsTripEndDate'] ? moment(filters?.['fmsTripEndDate']).format(this.DATE_FORMAT) : ''
    };

    removeEmptyParams(params);

    return this.http.get<{ pdfBase64: string }>(`${this.baseUrl}`, {params: {...params}})
      .pipe(
        map(response => {
          if (response && response.pdfBase64) {
            const timestamp = new Date().toISOString().replace(/[-:T]/g, '').split('.')[0];
            const fileName = `tram-trip_${timestamp}.pdf`;
            this.pdfService.savePdfFromBase64(response.pdfBase64, fileName);
          }
        }),
        catchError((error: HttpErrorResponse) => {
          throw new Error(error?.message || 'PDF generation failed');
        })
      );
  }
}
